import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { OvertimeReason } from "@/types/supabase-types";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/auth";

interface AddEditReasonModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: { id?: string; main_title: string; reason: string; code?: string }) => Promise<boolean>;
  isSubmitting: boolean;
  mode: 'add' | 'edit';
  reason?: OvertimeReason | null;
}

export function AddEditReasonModal({
  open,
  onOpenChange,
  onSubmit,
  isSubmitting,
  mode,
  reason
}: AddEditReasonModalProps) {
  const [mainTitle, setMainTitle] = useState("");
  const [reasonText, setReasonText] = useState("");
  const [customMainTitle, setCustomMainTitle] = useState("");
  const [isCustomMainTitle, setIsCustomMainTitle] = useState(false);
  
  const { user } = useAuth();

  // Fetch existing main titles for dropdown (filtered by company_id and is_active)
  const { data: existingMainTitles = [] } = useQuery({
    queryKey: ['overtime_reasons_main_titles', user?.companyId],
    queryFn: async () => {
      if (!user?.companyId) {
        toast.error('User company ID not available');
        return [];
      }

      const { data, error } = await supabase
        .from('overtime_reasons')
        .select('main_title')
        .eq('company_id', user.companyId)
        .eq('is_active', true)
        .order('main_title', { ascending: true });
      
      if (error) {
        toast.error('Failed to load existing categories');
        return [];
      }
      
      // Get unique main titles
      const uniqueTitles = [...new Set(data?.map((item: any) => item.main_title) || [])];
      return uniqueTitles;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    enabled: !!user?.companyId,
  });

  // Reset form when modal opens/closes or mode changes
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && reason) {
        setMainTitle(reason.main_title);
        setReasonText(reason.reason || '');
        setIsCustomMainTitle(false);
        setCustomMainTitle('');
      } else {
        setMainTitle('');
        setReasonText('');
        setIsCustomMainTitle(false);
        setCustomMainTitle('');
      }
    }
  }, [open, mode, reason]);

  const handleMainTitleChange = (value: string) => {
    if (value === 'custom') {
      setIsCustomMainTitle(true);
      setMainTitle('');
      setCustomMainTitle('');
    } else {
      setIsCustomMainTitle(false);
      setMainTitle(value);
      setCustomMainTitle('');
    }
  };

  const handleCustomMainTitleChange = (value: string) => {
    setCustomMainTitle(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const finalMainTitle = isCustomMainTitle ? customMainTitle : mainTitle;
    
    if (!finalMainTitle.trim()) {
      toast.error('Please select or enter a category');
      return;
    }
    
    if (!reasonText.trim()) {
      toast.error('Please enter a reason');
      return;
    }

    const submitData = {
      ...(mode === 'edit' && reason ? { id: reason.id } : {}),
      main_title: finalMainTitle.trim(),
      reason: reasonText.trim()
      // Remove code generation - let SQL function handle it
    };

    const success = await onSubmit(submitData);
    if (success) {
      // Form will be reset by the parent component
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {mode === 'add' ? 'Add New Overtime Reason' : 'Edit Overtime Reason'}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Category Selection */}
          <div className="space-y-2">
            <Label htmlFor="main-title">Category</Label>
            <Select value={isCustomMainTitle ? 'custom' : mainTitle} onValueChange={handleMainTitleChange}>
              <SelectTrigger>
                <SelectValue placeholder="Select category or add custom..." />
              </SelectTrigger>
              <SelectContent>
                {existingMainTitles.map((title) => (
                  <SelectItem key={title} value={title}>
                    {title}
                  </SelectItem>
                ))}
                <SelectItem value="custom" className="font-medium text-primary">
                  + Add Custom Category
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Custom Category Input */}
          {isCustomMainTitle && (
            <div className="space-y-2">
              <Label htmlFor="custom-main-title">Custom Category Name</Label>
              <Input
                id="custom-main-title"
                value={customMainTitle}
                onChange={(e) => handleCustomMainTitleChange(e.target.value)}
                placeholder="Enter custom category name..."
                required
              />
            </div>
          )}

          {/* Reason Input */}
          <div className="space-y-2">
            <Label htmlFor="reason">Reason</Label>
            <Input
              id="reason"
              value={reasonText}
              onChange={(e) => setReasonText(e.target.value)}
              placeholder="Enter specific reason..."
              required
            />
          </div>

          {/* Generated Code Preview - Updated */}
          {mainTitle && reasonText && (
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Generated Code</Label>
              <div className="text-sm font-mono bg-muted px-3 py-2 rounded-md text-muted-foreground">
                Code will be auto-generated
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="min-w-[100px]"
            >
              {isSubmitting ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                  <span>Saving...</span>
                </div>
              ) : (
                mode === 'add' ? 'Add Reason' : 'Update Reason'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
